import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  IconButton,
  Typography,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Button from "../../../../components/Button/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import FileUpload from "../../../../components/FileUpload/FileUpload";
import DataLoader from "../../../../shared/DataLoader/DataLoader";
import DownloadIcon from "../../../../assets/images/icons/download.svg";
import API from "../../../../api/axios";
import { useDispatch } from "react-redux";
import { setToast } from "../../../../store/slices/toastSlice";

interface RemarksDialogProps {
  open: boolean;
  loading?: boolean;
  initialValue?: string;
  initialFiles?: any[];
  mode?: "add" | "edit" | "view";
  userId?: string;
  positionId?: number | null;
  onClose: () => void;
  onSubmit: (remarks: string, files: any[], onSuccess?: () => void) => void;
  onDeleteClick?: (file: any, index: number | undefined, proceed: () => void) => void;
  title?: string;
  hideFileUpload?: boolean;
  submitLabel?: string;
}

const RemarksDialog: React.FC<RemarksDialogProps> = ({
  open,
  loading = false,
  initialValue = "",
  initialFiles = [],
  mode = "add",
  userId,
  positionId,
  onClose,
  onSubmit,
  onDeleteClick,
  title,
  hideFileUpload = false,
  submitLabel,
}) => {
  const [hasDeletedFiles, setHasDeletedFiles] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!open) {
      setHasDeletedFiles(false);
    }
  }, [open]);

  const formik = useFormik({
    initialValues: { remarks: initialValue, files: initialFiles },
    enableReinitialize: true,
    validationSchema: Yup.object({
      remarks: Yup.string()
        .trim()
        .required("Remarks is required")
        .min(
          title === "TA Remarks" ? 10 : 50,
          `Remarks must be at least ${title === "TA Remarks" ? 10 : 50} characters`,
        )
        .max(1000, "Remarks must be at most 1000 characters")
        .test(
          "no-dangerous-content",
          "Invalid characters <>,{} are not allowed",
          (value) => {
            if (!value) return false;
            const forbiddenPattern = /[<>{}]|javascript:/i;
            return !forbiddenPattern.test(value);
          }
        ),
      files: Yup.array()
        .when([], {
          is: () => !hideFileUpload,
          then: (schema) => schema.min(1, "Supporting documents are required").required("Supporting documents are required"),
          otherwise: (schema) => schema.notRequired(),
        })
        .test("max-new-files", "Maximum 5 new files can be uploaded at a time", function (value) {
          if (!value || !Array.isArray(value)) return true;
          const newFilesCount = value.filter((f) => f instanceof File).length;
          return newFilesCount <= 5;
        }),
    }),
    onSubmit: (values) => {
      onSubmit(values.remarks.trim(), values.files, () => {
        setHasDeletedFiles(false);
        formik.resetForm(); // Re-add form resetting on success
      });
    },
  });

  const handleDeleteClick = (file: any, index: number | undefined, proceed: () => void) => {
    setHasDeletedFiles(true);
    if (onDeleteClick) {
      onDeleteClick(file, index, proceed);
    }
  };

  const isRemarkChanged = formik.values.remarks.trim() !== initialValue.trim();
  const hasNewFiles = (formik.values.files || []).some((f: any) => f instanceof File);
  const totalFilesCount = (formik.values.files || []).length;
  const isDirty = isRemarkChanged || hasNewFiles || hasDeletedFiles;
  const canSubmit = formik.isValid && (hideFileUpload || totalFilesCount > 0) && isDirty;

  if (mode === "view") {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "12px", overflow: "visible" },
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: -15,
            top: -15,
            width: 32,
            height: 32,
            color: "#FB8B0E",
            border: "2px solid #FB8B0E",
            borderRadius: "50% !important",
            padding: "0px",
            backgroundColor: "#fff",
            zIndex: 1000,
            transition: "background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
            "@media (min-width: 900px)": {
              right: "-20px",
              top: "-20px",
              height: "44px",
              width: "44px",
            },
            "&:hover": {
              backgroundColor: "#f5f5f5",
            },
          }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <DialogTitle
          sx={{
            fontWeight: "bold",
            padding: "20px 24px 10px",
            borderBottom: "1px solid #EAEAEA",
          }}
        >
          Remarks
        </DialogTitle>
        <DialogContent sx={{ padding: "24px" }}>
          <Box
            sx={{
              backgroundColor: "#f0f5fa",
              padding: "16px",
              borderRadius: "8px",
              mt: 3,
              mb: 3,
            }}
          >
            <Typography variant="body1" sx={{ color: "#4A4A4A" }}>
              {initialValue || "No remarks available."}
            </Typography>
          </Box>
          
          <Box display="flex" justifyContent="flex-start">
            <Button
              variant="secondary"
              variantType="contained"
              label="Download Documents"
              isLoading={downloading}
              onClick={async () => {
                if (!userId || !positionId) return;
                setDownloading(true);
                try {
                  const response = await API.get(
                    `/upload/download-user-position_files?user_id=${userId}&position_id=${positionId}`,
                    { responseType: "blob" }
                  );
                  // The API returns the zip file directly.
                  // We handle it as a Blob so that the Authorization header (Bearer token) is correctly sent by Axios.
                  const blob = new Blob([response.data], {
                    type: response.headers["content-type"] as string || "application/zip",
                  });
                  // If the backend sends a content-disposition header with the filename, we could parse it here,
                  // but falling back to documents.zip works.
                  let filename = "documents.zip";
                  const disposition = response.headers["content-disposition"];
                  if (disposition && disposition.indexOf('attachment') !== -1) {
                      var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                      var matches = filenameRegex.exec(disposition);
                      if (matches != null && matches[1]) { 
                        filename = matches[1].replace(/['"]/g, '');
                      }
                  }
                  
                  const blobUrl = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = blobUrl;
                  link.download = filename;
                  // Append to html link element page and trigger download
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(blobUrl);
                } catch (error: any) {
                  // In case the API returns a JSON error inside the Blob, we attempt to read it
                  let errorMessage = "Failed to download documents.";
                  if (error.response && error.response.data instanceof Blob) {
                    try {
                      const textData = await error.response.data.text();
                      const errorJson = JSON.parse(textData);
                      if (errorJson?.message) errorMessage = errorJson.message;
                      else if (errorJson?.errors?.[0]?.message) errorMessage = errorJson.errors[0].message;
                    } catch (e) {}
                  }
                  
                  dispatch(
                    setToast({
                      message: errorMessage,
                      severity: "error",
                      open: true,
                    })
                  );
                } finally {
                  setDownloading(false);
                }
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose();
        formik.resetForm();
        setHasDeletedFiles(false);
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {title ? title : mode === "edit" ? "Edit Remarks" : "Add Remarks"}
      </DialogTitle>

      <form onSubmit={formik.handleSubmit} style={{ position: "relative" }}>
        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.4)",
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <DataLoader />
          </Box>
        )}
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            name="remarks"
            value={formik.values.remarks}
            onChange={(e) => {
              formik.handleChange(e);
              if (formik.touched.remarks) {
                formik.validateField('remarks');
              }
            }}
            onBlur={formik.handleBlur}
            error={Boolean(formik.touched.remarks && formik.errors.remarks)}
            helperText={(formik.touched.remarks && formik.errors.remarks) as string}
            placeholder="Enter remarks"
            sx={{ mb: 3 }}
          />

          {!hideFileUpload && (
            <Box sx={{ width: "100%", maxWidth: "100%" }}>
              <FileUpload
                label="Supporting Documents*"
                acceptedFormat="PDF or JPG or JPEG or PNG"
                maxSizeMB={10}
                multiple={true}
                deferUpload={true}
                initialFile={formik.values.files}
                onFileSelect={(files) => formik.setFieldValue("files", files)}
                onDeleteClick={handleDeleteClick}
                error={Boolean(formik.errors.files)}
                helperText={typeof formik.errors.files === "string" ? formik.errors.files : undefined}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button
            variant="secondary"
            variantType="outlined"
            label="Cancel"
            onClick={() => {
              onClose();
              formik.resetForm();
              setHasDeletedFiles(false);
            }}
          />
          <Button
            variant="secondary"
            variantType="contained"
            label={submitLabel ? submitLabel : "Submit"}
            type="submit"
            disabled={!canSubmit || loading}
          />
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RemarksDialog;
